import React, { useState } from 'react';

interface Team {
  name: string;
  shortname: string;
  img?: string;
}

interface ManualTeamsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (teams: Team[]) => void;
}

const ManualTeamsModal: React.FC<ManualTeamsModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [teams, setTeams] = useState<Team[]>([{ name: '', shortname: '', img: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addTeam = () => {
    setTeams([...teams, { name: '', shortname: '', img: '' }]);
  };

  const removeTeam = (index: number) => {
    if (teams.length > 1) {
      setTeams(teams.filter((_, i) => i !== index));
    }
  };

  const updateTeam = (index: number, field: keyof Team, value: string) => {
    const updatedTeams = [...teams];
    updatedTeams[index] = { ...updatedTeams[index], [field]: value };
    setTeams(updatedTeams);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const validTeams = teams.filter(team => team.name.trim() && team.shortname.trim());
    
    if (validTeams.length === 0) {
      alert('Please add at least one team with name and shortname');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(validTeams);
      setTeams([{ name: '', shortname: '', img: '' }]);
      onClose();
    } catch (error) {
      console.error('Error adding teams:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Teams Manually</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {teams.map((team, index) => (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Team {index + 1}</h3>
                {teams.length > 1 && (
                  <button
                    onClick={() => removeTeam(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={team.name}
                    onChange={(e) => updateTeam(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Mumbai Indians"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Short Name *
                  </label>
                  <input
                    type="text"
                    value={team.shortname}
                    onChange={(e) => updateTeam(index, 'shortname', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., MI"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={team.img || ''}
                    onChange={(e) => updateTeam(index, 'img', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <button
            onClick={addTeam}
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 mr-2"
          >
            + Add Another Team
          </button>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Add Teams'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualTeamsModal; 